import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useSegmentMembersQuery } from '@/client/api/campaigns/lifecycle.query';
import { useDebouncedSearch } from '@/client/hooks/useDebouncedSearch';

const PAGE = 25;
const label = (k: string) => k.replace(/_/g, ' ');

/** Who is in a saved segment right now, resolved the way a campaign would. */
export default function SegmentMembersPanel({ sid }: { sid: string }) {
  const { value: q, setValue: setQ, debounced } = useDebouncedSearch('', { delay: 300 });
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isError, isFetching } = useSegmentMembersQuery(sid, {
    q: debounced,
    limit: PAGE,
    offset,
  });
  const members = data?.members ?? [];
  // A few uploaded columns are enough to recognise a row; the rest are on export.
  const columns = (data?.columns ?? []).slice(0, 3);
  const total = data?.total ?? 0;

  return (
    <div className="space-y-3 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Who is in this segment</h2>
          <p className="text-xs text-gray-500">
            {isLoading ? 'Working out who is in it…' : `${total.toLocaleString()} people right now`}
            {isFetching && !isLoading && <Loader2 className="ml-1.5 inline h-3 w-3 animate-spin" />}
          </p>
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOffset(0);
            }}
            placeholder="Search name, email, phone or any column"
            className="h-9 w-full rounded-md border border-gray-300 pl-9 pr-3 text-sm"
          />
        </div>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      )}
      {isError && <p className="text-sm text-red-600">Could not load the members.</p>}
      {!isLoading && !isError && members.length === 0 && (
        <p className="py-6 text-center text-sm text-gray-500">
          {q ? 'Nobody matches that search.' : 'Nobody is in this segment right now.'}
        </p>
      )}

      {members.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Phone</th>
                {columns.map((c) => (
                  <th key={c} className="px-3 py-2 font-medium">
                    {label(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.map((m, i) => (
                <tr key={`${m.uid ?? m.email ?? m.phone}-${i}`}>
                  <td className="px-3 py-2">
                    <div className="text-gray-900">{m.name || <span className="text-gray-400">No name</span>}</div>
                    {m.uid ? <div className="font-mono text-[11px] text-gray-500">uid {m.uid}</div> : null}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{m.email || <span className="text-gray-400">None</span>}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                    {m.phone || <span className="text-gray-400">None</span>}
                  </td>
                  {columns.map((c) => (
                    <td key={c} className="max-w-[220px] truncate px-3 py-2 text-gray-600">
                      {m.fields?.[c] || ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            {(offset + 1).toLocaleString()}–{Math.min(offset + PAGE, total).toLocaleString()} of{' '}
            {total.toLocaleString()}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE >= total}
              onClick={() => setOffset(offset + PAGE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
